<template>
    <div class="progress-bar"
         :style="containerStyle">
        <div class="progress"
             :style="progressStyle" />
        <div v-if="!hideProgressLines"
             class="progress-lines">
            <div v-for="i in goalStreak"
                 :key="i"
                 class="progress-line"
                 :style="lineStyle(i)" />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed } from 'vue';
    import { MAX_STREAK } from '../../constants/streak';

    interface Props {
        streakCompleted?: number;
        goalStreak?: number;
        hideProgressLines?: boolean;
        height?: string;
    }

    const props = withDefaults(defineProps<Props>(), {
        streakCompleted: 0,
        goalStreak: MAX_STREAK,
        hideProgressLines: false,
        height: '20px',
    });

    const progressPercentage = computed(() => (props.streakCompleted / props.goalStreak) * 100);
    const progressStyle = computed(() => ({
        '--progress-width': `${progressPercentage.value || 0}%`,
    }));
    const containerStyle = computed(() => ({
        height: props.height,
    }));

    const lineStyle = (i: number): Record<string, string> => ({
        left: `${i * (100 / props.goalStreak)}%`,
        height: '100%',
        width: '1px',
        position: 'absolute',
    });
</script>

<style scoped lang="scss">
@use "@/sass/partials/variables" as legacyVariables;
@use "@/sass/design-system/variables" as variables;

.progress-bar {
    position: relative;
    overflow: hidden;
    width: 100%;
    border-radius: 10px;
    background-color: legacyVariables.$star-rating-inactive;

    .progress-lines {
        position: absolute;
        top: 0;
        bottom: 0;
        display: flex;
        width: 100%;
        justify-content: space-between;

        .progress-line {
            background-color: variables.$baseWhite;
        }

        .progress-line::before {
            content: "";
        }
    }

    .progress {
        width: 0;
        height: 100%;
        border-radius: unset;
        animation: load-progress 2s ease forwards;
        background-color: variables.$primary500;
    }

    @keyframes load-progress {
        from {
            width: 0;
        }
        to {
            width: var(--progress-width, 0);
        }
    }
}
</style>
